import { useMemo, useState } from 'react';
import { Bell, Plus, Copy, Edit2, Trash2, Search } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, type ColumnDef, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useNotificationCallbacks,
  useCreateNotification,
  useUpdateNotification,
  useDeleteNotification,
} from '@/api/media/vod-notifications';
import type { NotificationsCallback } from '@/api/media/vod-notifications-types';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import { RefreshButton } from '../../_shared/AnalyticsKit';
import styles from './NotificationsList.module.css';

// ─── Callback create / edit dialog ───────────────────────────────────────────

function CallbackDialog({
  serviceId,
  editRow,
  onClose,
  onSaved,
}: {
  serviceId: string;
  editRow?: NotificationsCallback;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isEdit = !!editRow;

  const [name, setName] = useState(editRow?.name ?? '');
  const [url, setUrl] = useState(editRow?.url ?? '');
  const [urlError, setUrlError] = useState('');

  const createMutation = useCreateNotification(serviceId);
  const updateMutation = useUpdateNotification(serviceId);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const validateUrl = (value: string): string => {
    if (!value) return 'URL is required.';
    try {
      const u = new URL(value);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'URL must start with http:// or https://';
      return '';
    } catch {
      return 'Please enter a valid URL (e.g. https://example.com/webhook)';
    }
  };

  const handleSubmit = async () => {
    const err = validateUrl(url);
    if (err) { setUrlError(err); return; }

    try {
      if (isEdit && editRow) {
        await updateMutation.mutateAsync({ id: String(editRow.id), req: { name, url } });
        toast.success('Notification callback updated');
      } else {
        await createMutation.mutateAsync({ name, url });
        toast.success('Notification callback created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error((e as Error).message || `Failed to ${isEdit ? 'update' : 'create'} callback`);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Notification Callback' : 'Add Notification Callback'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className={styles.formStack}>
            <Field label="Name">
              {(p) => (
                <Input
                  {...p}
                  placeholder="e.g. My Webhook"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Webhook URL" required error={urlError || undefined}>
              {(p) => (
                <Input
                  {...p}
                  placeholder="https://your-domain.com/webhook"
                  value={url}
                  invalid={!!urlError}
                  onChange={(e) => { setUrl(e.target.value); if (urlError) setUrlError(''); }}
                />
              )}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={isPending}>Cancel</Button>
          </DialogClose>
          <Button variant="primary" disabled={isPending || !url} onClick={handleSubmit}>
            {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main list ────────────────────────────────────────────────────────────────

export function VodNotifications({ serviceId }: { serviceId: string }) {
  const toast = useToast();

  const { data: callbacks = [], isLoading, refetch } = useNotificationCallbacks(serviceId);
  const deleteMutation = useDeleteNotification(serviceId);

  const [dialogState, setDialogState] = useState<{ open: boolean; editRow?: NotificationsCallback }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<NotificationsCallback | null>(null);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    if (!search.trim()) return callbacks;
    const q = search.toLowerCase();
    return callbacks.filter((c) => (c.name ?? '').toLowerCase().includes(q) || (c.url ?? '').toLowerCase().includes(q));
  }, [callbacks, search]);

  const openCreate = () => setDialogState({ open: true });
  const openEdit = (row: NotificationsCallback) => setDialogState({ open: true, editRow: row });
  const closeDialog = () => setDialogState({ open: false });

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(String(deleteTarget.id), {
      onSuccess: () => { toast.success('Callback deleted'); setDeleteTarget(null); refetch(); },
      onError: (err) => { toast.error((err as Error).message || 'Failed to delete callback'); setDeleteTarget(null); },
    });
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const columns: ColumnDef<NotificationsCallback, unknown>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 80,
      cell: ({ row }) => <span className={styles.cellMono}>{row.original.id}</span>,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 180,
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)' }}>
          {row.original.name || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'url',
      header: 'Webhook URL',
      cell: ({ row }) => (
        <span
          className={styles.urlCell}
          title={row.original.url}
          onDoubleClick={() => copyUrl(row.original.url)}
        >
          {row.original.url}
        </span>
      ),
    },
    {
      accessorKey: 'created',
      header: 'Created',
      size: 160,
      cell: ({ row }) => (
        <span className={styles.cellMono}>
          {row.original.created ? new Date(row.original.created).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 110,
      cell: ({ row }) => (
        <div className={styles.actionRow}>
          <IconButton size="sm" variant="ghost" aria-label="Copy URL" onClick={(e) => { e.stopPropagation(); copyUrl(row.original.url); }}>
            <Copy size={13} />
          </IconButton>
          <IconButton size="sm" variant="ghost" aria-label="Edit callback" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Edit2 size={13} />
          </IconButton>
          <IconButton size="sm" variant="danger" aria-label="Delete callback" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 size={13} />
          </IconButton>
        </div>
      ),
    },
  ];

  const loading = isLoading;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Notifications' }]} />}
        title={`Notifications${callbacks.length ? ` (${callbacks.length})` : ''}`}
        description="Manage webhook URLs for asynchronous encoding job notifications."
        actions={
          <Button variant="primary" startIcon={<Plus size={14} />} onClick={openCreate}>
            Add Callback
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search by name or URL…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
        end={<RefreshButton onRefresh={() => void refetch()} />}
      />

      {loading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          onRowClick={(row) => openEdit(row)}
          emptyState={
            // First-use shows a creation hint; once callbacks exist the empty
            // state means the search excluded everything.
            callbacks.length === 0 ? (
              <EmptyState
                icon={<Bell size={20} />}
                title="No notification callbacks"
                description="Add a webhook URL to receive job status notifications."
                actions={
                  <Button size="sm" startIcon={<Plus size={14} />} onClick={openCreate}>
                    Add Callback
                  </Button>
                }
              />
            ) : (
              'No callbacks match your search.'
            )
          }
          footer={callbacks.length ? `Showing ${rows.length} of ${callbacks.length}` : undefined}
        />
      )}

      {dialogState.open && (
        <CallbackDialog
          serviceId={serviceId}
          editRow={dialogState.editRow}
          onClose={closeDialog}
          onSaved={() => refetch()}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete Notification Callback"
        description={`This will permanently delete callback "${deleteTarget?.name || deleteTarget?.id}". This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        requireText={deleteTarget?.name || String(deleteTarget?.id ?? '')}
      />
    </Container>
  );
}
